import { useEffect, useState, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useMe } from "@/lib/authz";
import { Plus, Link as LinkIcon, MagicWand, Lock, LockOpen, ArrowsClockwise } from "@phosphor-icons/react";
import ScopeFilterBar, { scopeToQuery } from "@/components/ScopeFilterBar";

const EMPTY_ACTIVITY = { company: "", job_title: "", application_url: "", notes: "" };

export default function DailyActivity() {
  const { hasPerm, canSeeOwner } = useMe();
  const [items, setItems] = useState([]);
  const [cands, setCands] = useState([]);
  const [candidateId, setCandidateId] = useState("");
  const [locked, setLocked] = useState(false);
  const [activity, setActivity] = useState(EMPTY_ACTIVITY);
  const [preview, setPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState({});

  const load = useCallback(() => {
    const p = new URLSearchParams({ limit: 200, ...scopeToQuery(filters) }).toString();
    return api.get(`/applications?${p}`).then((r) => setItems(r.data));
  }, [filters]);
  useEffect(() => {
    load();
    api.get("/candidates").then((r) => {
      setCands(r.data);
      // Prefill candidate from ?candidate=<id> when coming from Hotlist
      const pre = searchParams.get("candidate");
      if (pre && r.data.find((c) => c.id === pre)) {
        setCandidateId(pre);
        setLocked(true);
      }
    });
  }, [load, searchParams]);

  useEffect(() => {
    if (!activity.application_url) { setPreview(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/classify-url", { url: activity.application_url });
        setPreview(data);
      } catch { setPreview(null); }
    }, 300);
    return () => clearTimeout(t);
  }, [activity.application_url]);

  const candidateOptions = useMemo(() =>
    cands.filter((c) =>
      (c.service_status === "Active" || c.service_status === "Resumed") &&
      // Phase A/B/C/D — filter the dropdown by dual check so users only see
      // candidates they can actually submit activities for.
      hasPerm("candidate.edit") && canSeeOwner(c.assigned_employee_id)
    ),
    [cands, hasPerm, canSeeOwner]);

  const currentCandidate = cands.find((c) => c.id === candidateId);
  const canSubmit = currentCandidate &&
    hasPerm("candidate.edit") &&
    canSeeOwner(currentCandidate.assigned_employee_id);

  const submit = async (e) => {
    e.preventDefault();
    if (!candidateId) { toast.error("Select a candidate first"); return; }
    setSubmitting(true);
    try {
      await api.post("/applications", { candidate_id: candidateId, ...activity });
      toast.success(`Recorded for ${currentCandidate?.full_name || "candidate"}`);
      // Rapid mode: keep candidate + clear only activity fields
      setActivity(EMPTY_ACTIVITY);
      setPreview(null);
      load();
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  const changeCandidate = () => {
    setCandidateId("");
    setLocked(false);
    setActivity(EMPTY_ACTIVITY);
    setPreview(null);
  };

  const toggleLock = () => {
    if (!candidateId) { toast.error("Select a candidate to lock"); return; }
    setLocked((v) => !v);
  };

  return (
    <div className="space-y-5" data-testid="daily-activity-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold">Daily Activity</h2>
          <p className="text-sm text-slate-500">Rapid entry — lock a candidate to log multiple applications back-to-back.</p>
        </div>
      </div>

      <ScopeFilterBar value={filters} onChange={setFilters}
                      candidates={cands}
                      showStatus={false}
                      testid="daily-activity-scope" />

      {/* Rapid entry panel */}
      <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-4" data-testid="rapid-entry-panel">
        <div className="flex items-end gap-3 flex-wrap">
          <div className="flex-1 min-w-[240px]">
            <label className="text-xs text-slate-500">Candidate {locked && <span className="text-emerald-700">(locked)</span>}</label>
            {locked && currentCandidate ? (
              <div className="border border-emerald-300 bg-emerald-50 rounded-md px-3 py-2 text-sm font-medium text-emerald-800 flex items-center justify-between" data-testid="locked-candidate">
                <span>{currentCandidate.full_name} · <span className="text-emerald-700 text-xs">{currentCandidate.email}</span></span>
              </div>
            ) : (
              <select
                required
                value={candidateId}
                onChange={(e) => setCandidateId(e.target.value)}
                data-testid="rapid-candidate-select"
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
              >
                <option value="">Select candidate…</option>
                {candidateOptions.map((c) => <option key={c.id} value={c.id}>{c.full_name}</option>)}
              </select>
            )}
          </div>
          <button type="button" onClick={toggleLock} data-testid="lock-toggle"
                  className={`text-sm px-3 py-2 rounded-md border inline-flex items-center gap-1 ${locked ? "bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700" : "border-slate-200 hover:bg-slate-50"}`}>
            {locked ? <><Lock size={14} /> Locked</> : <><LockOpen size={14} /> Lock candidate</>}
          </button>
          {locked && (
            <button type="button" onClick={changeCandidate} data-testid="change-candidate-btn"
                    className="text-sm px-3 py-2 rounded-md border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-1">
              <ArrowsClockwise size={14} /> Change candidate
            </button>
          )}
        </div>

        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <input required placeholder="Company" value={activity.company}
                 onChange={(e) => setActivity((f) => ({ ...f, company: e.target.value }))}
                 data-testid="rapid-company"
                 className="border border-slate-200 rounded-md px-3 py-2 text-sm" />
          <input required placeholder="Job title" value={activity.job_title}
                 onChange={(e) => setActivity((f) => ({ ...f, job_title: e.target.value }))}
                 data-testid="rapid-title"
                 className="border border-slate-200 rounded-md px-3 py-2 text-sm" />
          <input required type="url" placeholder="Application URL" value={activity.application_url}
                 onChange={(e) => setActivity((f) => ({ ...f, application_url: e.target.value }))}
                 data-testid="rapid-url"
                 className="md:col-span-2 border border-slate-200 rounded-md px-3 py-2 text-sm" />
          <input placeholder="Notes (optional)" value={activity.notes}
                 onChange={(e) => setActivity((f) => ({ ...f, notes: e.target.value }))}
                 className="md:col-span-3 border border-slate-200 rounded-md px-3 py-2 text-sm" />
          <button type="submit" disabled={submitting || !candidateId} data-testid="rapid-submit"
                  className="bg-sky-600 hover:bg-sky-700 disabled:opacity-60 text-white rounded-md px-4 py-2 text-sm inline-flex items-center justify-center gap-1">
            <Plus size={14} /> {submitting ? "Saving…" : "Submit"}
          </button>
        </form>
        {preview && (
          <div className="text-xs text-slate-600 flex items-center gap-2" data-testid="url-preview">
            <MagicWand size={12} className="text-sky-600" />
            Auto-detected: <b>{preview.type}</b> · {preview.label}
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 uppercase text-xs tracking-widest">
            <tr>
              <th className="text-left px-4 py-3">Time</th>
              <th className="text-left px-4 py-3">Candidate</th>
              <th className="text-left px-4 py-3">Company / Role</th>
              <th className="text-left px-4 py-3">Type</th>
              <th className="text-left px-4 py-3">Portal</th>
              <th className="text-left px-4 py-3">Employee</th>
              <th className="text-right px-4 py-3">Link</th>
            </tr>
          </thead>
          <tbody>
            {items.map((a) => {
              const c = cands.find((x) => x.id === a.candidate_id);
              return (
                <tr key={a.id} className="border-t border-slate-100 hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-3 text-slate-500 text-xs">{new Date(a.submission_time).toLocaleString()}</td>
                  <td className="px-4 py-3">{c?.full_name || "—"}</td>
                  <td className="px-4 py-3"><b>{a.company}</b><div className="text-xs text-slate-500">{a.job_title}</div></td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${a.app_type === "Easy Apply" ? "bg-amber-50 text-amber-700 border border-amber-200" : "bg-sky-50 text-sky-700 border border-sky-200"}`}>{a.app_type}</span>
                  </td>
                  <td className="px-4 py-3 text-slate-600 text-xs">{a.portal_label}</td>
                  <td className="px-4 py-3 text-slate-600">{a.employee_name}</td>
                  <td className="px-4 py-3 text-right">
                    <a href={a.application_url} target="_blank" rel="noreferrer" className="text-sky-700 hover:underline inline-flex items-center gap-1 text-xs">
                      <LinkIcon size={12} /> Open
                    </a>
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && <tr><td colSpan={7} className="text-center text-slate-400 py-10">No submissions yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
